// 📁 src/components/CardBlock.tsx
import { useState, useEffect, useRef } from "react";
import useCardsPerPage from "./usePerPage/UsePerPage";
import Loader from "./loaderAndError/Loader";
import ErrorDisplay from "./loaderAndError/ErrorDisplay";
import ProfessionCard from "./Card/ProfessionCard";
import ProfesionalCard from "./Card/ProfesionalCard";
import SeccionCard from "./Card/SeccionCard";
import Pagination from "./pagination/Pagination";
import { CardBlockProps, Profesion, PerfilProfesional, Seccion } from "@/types";
import MensajeEstamosEnEllo from "../../common/MensajeEstamosEnEllo";
import { useSearch, SearchItem } from "./buscarFiltrar/SearchContext";
import { safeFetch } from "@/api/safeFetch";
import { SvgLupa } from "@/icons";
import LocLocalizacionFiltro from "./buscarFiltrar/LocalizacionFiltro";
import { LocationType } from "./types/LocationType";
import SubscriptionActivity from "@/components/subscriptionActivity/SubscriptionActivity";
import RevenueActivity from "@/components/revenueActivity/RevenueActivity";

const CardBlock: React.FC<CardBlockProps> = ({
  servicio,
  seccion,
  className,
}) => {
  const [perfiles, setPerfiles] = useState<PerfilProfesional[]>([]);
  const [profesiones, setProfesiones] = useState<Profesion[]>([]);
  const [secciones, setSecciones] = useState<Seccion[]>([]);
  const [filteredItems, setFilteredItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const {
    query: searchQuery,
    results,
    registerItem,
    unregisterItem,
  } = useSearch();
  const cardsPerPage = useCardsPerPage();
  const cardBlockRef = useRef<HTMLDivElement>(null);
  const prevPageRef = useRef(currentPage);
  const headerHeight = 100;
  const [locationType, setLocationType] = useState<LocationType>("localidad");
  const [locationValue, setLocationValue] = useState("");

  // ==========================
  // 🔍 Registro global en SearchContext
  // ==========================
  useEffect(() => {
    const allItems: SearchItem[] = [];

    secciones.forEach((s) =>
      allItems.push({
        id: `seccion-${s.id}`,
        title: s.nombreSeccion ?? "",
        content: s.descripcion ?? "",
        type: "seccion",
        path: `/seccion/${s.id}`,
      }),
    );

    profesiones.forEach((p) =>
      allItems.push({
        id: `profesion-${p.id}`,
        title: p.nombre_profesion ?? "",
        content: p.descripcion ?? "",
        type: "profesion",
        path: `/profesion/${p.id}`,
        tags: [seccion ?? ""],
      }),
    );

    perfiles.forEach((p) => {
      const profesion = profesiones.find((pr) => pr.id === p.profesion_id);

      allItems.push({
        id: `perfil-${p.perfil_profesional_id}`,
        title:
          p.nombre_comercial?.trim() ||
          p.nombre_empresa ||
          profesion?.nombre_profesion ||
          "",

        content: `
      ${p.descripcion ?? ""}
      ${p.nombre_empresa ?? ""}
      ${p.nombre_comercial ?? ""}
    `,

        type: "perfil",
        path: `/profesional/${servicio}/${p.perfil_profesional_id}`,

        tags: [
          profesion?.nombre_profesion ?? "",
          servicio ?? "",
          p.nombre_empresa ?? "",
          p.nombre_comercial ?? "",
        ],
      });
    });

    allItems.forEach(registerItem);
    return () => allItems.forEach((i) => unregisterItem(i.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secciones, profesiones, perfiles, servicio, seccion]);

  useEffect(() => setCurrentPage(1), [searchQuery]);

  useEffect(() => {
    if (currentPage !== prevPageRef.current && cardBlockRef.current) {
      const offsetTop = cardBlockRef.current.offsetTop - headerHeight;
      window.scrollTo({ top: offsetTop, behavior: "smooth" });
      prevPageRef.current = currentPage;
    }
  }, [currentPage]);

  // ==========================
  // 📡 Fetch dinámico
  // ==========================
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        secciones.forEach((s) => unregisterItem(`seccion-${s.id}`));
        profesiones.forEach((p) => unregisterItem(`profesion-${p.id}`));
        perfiles.forEach((p) =>
          unregisterItem(`perfil-${p.perfil_profesional_id}`),
        );

        if (servicio) {
          const res = await safeFetch(
            `/api/perfiles/${encodeURIComponent(servicio)}`,
          );
          if (res.status === 404) {
            setPerfiles([]);
          } else {
            const data = await res.json();
            const arr = Array.isArray(data) ? data : [];
            const sorted = arr.sort((a: any, b: any) => {
              const aName = (a.nombre_empresa ?? a.nombre_profesion ?? "")
                .toString()
                .trim();
              const bName = (b.nombre_empresa ?? b.nombre_profesion ?? "")
                .toString()
                .trim();
              return aName.localeCompare(bName, "es", { sensitivity: "base" });
            });
            setPerfiles(sorted);
          }
        } else if (seccion) {
          const res = await safeFetch(
            `/api/profesiones/${encodeURIComponent(seccion)}`,
          );
          if (res.status === 404) {
            setProfesiones([]);
          } else {
            const data = await res.json();
            const arr = Array.isArray(data) ? data : [];

            const sorted = arr.sort((a: any, b: any) => {
              const prioridad = "cuadranex s.l";

              const aNombre = (a.nombre_profesion ?? "")
                .toString()
                .trim()
                .toLowerCase();
              const bNombre = (b.nombre_profesion ?? "")
                .toString()
                .trim()
                .toLowerCase();

              if (aNombre === prioridad) return -1;
              if (bNombre === prioridad) return 1;

              return aNombre.localeCompare(bNombre, "es", {
                sensitivity: "base",
              });
            });

            setProfesiones(sorted);
          }
        } else {
          const res = await safeFetch(`/api/seccion`);
          if (res.status === 404) {
            setSecciones([]);
          } else {
            const data = await res.json();

            const arr = Array.isArray(data) ? data : [];
            const sorted = arr.sort((a: any, b: any) =>
              (a.nombreSeccion ?? "")
                .toString()
                .trim()
                .localeCompare(
                  (b.nombreSeccion ?? "").toString().trim(),
                  "es",
                  { sensitivity: "base" },
                ),
            );
            setSecciones(sorted);
          }
        }
      } catch (err: any) {
        if (!navigator.onLine)
          setError("Sin conexión a internet. Revisa tu red.");
        else setError("Error al obtener datos del servidor.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [servicio, seccion]);

  // ==========================
  // 🔍 Filtrado local
  // ==========================
  useEffect(() => {
    const lowerQuery = searchQuery.toLowerCase().trim();
    const lowerLocation = locationValue.toLowerCase().trim();

    const items = servicio ? perfiles : seccion ? profesiones : secciones;

    const resultsLocal = (items as any[]).filter((item) => {
      const searchText = `
    ${item.nombre_empresa ?? ""} 
    ${item.nombre_comercial ?? ""} 
    ${item.nombre_profesion ?? ""} 
    ${item.nombreSeccion ?? ""} 
    ${item.descripcion ?? ""}
  `.toLowerCase();

      const matchesSearch = !lowerQuery || searchText.includes(lowerQuery);

      let matchesLocation = true;

      if (lowerLocation && typeof item.direccion === "object") {
        if (locationType === "localidad") {
          matchesLocation = item.direccion?.localidad
            ?.toLowerCase()
            .includes(lowerLocation);
        } else if (locationType === "provincia") {
          matchesLocation =
            item.direccion?.provincia?.toLowerCase() === lowerLocation;
        } else if (locationType === "provincia-localidad") {
          matchesLocation =
            item.direccion?.provincia?.toLowerCase() === lowerLocation;
        } else if (locationType === "codigo_postal") {
          matchesLocation = item.direccion?.codigo_postal
            ?.toLowerCase()
            .includes(lowerLocation);
        }
      }

      return matchesSearch && matchesLocation;
    });

    setFilteredItems(resultsLocal);
  }, [
    searchQuery,
    locationValue,
    locationType,
    perfiles,
    profesiones,
    secciones,
    servicio,
    seccion,
  ]);

  // ==========================
  // 🔹 Preparar items a renderizar
  // ==========================
  const useGlobalSearch = Boolean(searchQuery && searchQuery.trim().length > 0);
  const globalResults: SearchItem[] = useGlobalSearch ? results : [];
  const itemsToRender: any[] = useGlobalSearch
    ? globalResults.map((it) => {
        const type =
          it.type ??
          (it.path?.includes("/profesional/")
            ? "perfil"
            : it.path?.includes("/profesion/")
              ? "profesion"
              : it.path?.includes("/seccion/")
                ? "seccion"
                : "unknown");
        if (type === "perfil") {
          const idFromId = it.id?.toString().startsWith("perfil-")
            ? parseInt(it.id.toString().replace(/^perfil-/, ""), 10)
            : undefined;
          const perfilId =
            idFromId ??
            (it.path
              ? parseInt(it.path.split("/").pop() as string, 10)
              : undefined);
          const servicioFromTags =
            Array.isArray(it.tags) && it.tags.length >= 2
              ? it.tags[1]
              : undefined;
          return {
            perfil_profesional_id: perfilId,
            nombre_empresa: it.title,
            descripcion: it.content,
            _meta: it,
            _servicio: servicioFromTags ?? servicio,
          };
        } else if (type === "profesion") {
          const idFromId = it.id?.toString().startsWith("profesion-")
            ? parseInt(it.id.toString().replace(/^profesion-/, ""), 10)
            : undefined;
          return {
            id: idFromId,
            nombre_profesion: it.title,
            descripcion: it.content,
            _meta: it,
          };
        } else if (type === "seccion") {
          const idFromId = it.id?.toString().startsWith("seccion-")
            ? parseInt(it.id.toString().replace(/^seccion-/, ""), 10)
            : undefined;
          return {
            id: idFromId,
            nombreSeccion: it.title,
            descripcion: it.content,
            _meta: it,
          };
        } else {
          return { _meta: it, id: it.id, title: it.title, content: it.content };
        }
      })
    : filteredItems;

  const totalPages = Math.max(
    1,
    Math.ceil(itemsToRender.length / cardsPerPage),
  );
  const indexOfLast = currentPage * cardsPerPage;
  const indexOfFirst = indexOfLast - cardsPerPage;

  const currentItems = itemsToRender.slice(indexOfFirst, indexOfLast);

  const paginate = (pageNumber: number) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setLoading(true);
      setCurrentPage(pageNumber);
      setTimeout(() => setLoading(false), 200);
    }
  };

  if (useGlobalSearch) {
    return (
      <div className="w-full flex flex-col items-center justify-center text-center text-zinc-800 py-10">
        <SvgLupa className="w-40 h-40 sm:w-28 sm:h-28 md:w-72 md:h-72 text-zinc-600 mb-4" />
        <div className="text-lg sm:text-xl md:text-2xl font-semibold italic text-zinc-700">
          Mostrando resultados
        </div>
      </div>
    );
  }

  return (
    <div
      ref={cardBlockRef}
      className={`relative flex flex-col md:mx-6 xxl:mx-9 xxl:mt-15 items-center justify-center h-full sm:mx-4 xs:mx-2 xs:mt-20 md:mt-20 ${className}`}
      style={{ overflowY: "hidden" }}
    >
      {/* Loader */}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-50">
          <Loader />
        </div>
      )}

      {/* Header */}
      {!loading && (
        <div className="flex flex-col md:flex-row md:flex-nowrap justify-between items-center text-start w-full px-4 gap-3">
          <h1 className="xs:text-xl xxl:text-5xl sm:text-4xl md:text-xl lg:text-3xl font-semibold text-black w-full md:w-auto md:flex-grow">
            {servicio
              ? `Profesionales en ${servicio}`
              : seccion
                ? `Elige tu profesión en ${seccion}`
                : "Elige tus servicios"}
          </h1>

          <div className="flex flex-wrap items-center justify-end gap-3 w-full md:w-auto md:shrink-0">
            {servicio && (
              <LocLocalizacionFiltro
                filterType={locationType}
                filterValue={locationValue}
                onTypeChange={(v) => setLocationType(v as any)}
                onValueChange={setLocationValue}
              />
            )}

            {/* Contadores únicamente en la página principal */}
            {!servicio && !seccion && (
              <>
                <SubscriptionActivity />
                <RevenueActivity />
              </>
            )}
          </div>
        </div>
      )}

      {/* Error */}
      {error && <ErrorDisplay message={error} />}
      {/* Contenido principal */}
      {!loading && !error && (
        <>
          {itemsToRender.length === 0 ? (
            <MensajeEstamosEnEllo servicio={servicio} />
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 w-full max-w-screen-xl mx-auto place-items-stretch mt-10">
                {currentItems
                  .filter((item) =>
                    servicio ? item.perfil_profesional_id : true,
                  )
                  .map((item, index) => {
                    if (servicio)
                      return (
                        <ProfesionalCard
                          key={item.perfil_profesional_id}
                          profesional={{
                            ...item,
                            descripcion: item.descripcion ?? "",
                          }}
                          servicio={servicio}
                        />
                      );
                    if (seccion)
                      return (
                        <ProfessionCard
                          key={`pr-${item.id ?? index}`}
                          profesion={item as Profesion}
                        />
                      );
                    return (
                      <SeccionCard
                        key={`s-${item.id ?? index}`}
                        seccion={item as Seccion}
                      />
                    );
                  })}
              </div>

              {itemsToRender.length > cardsPerPage && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={paginate}
                />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default CardBlock;
